import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { SmartCaptcha } from "@yandex/smart-captcha";
import "./login.scss";
import AppUpdateGate from "./AppUpdateGate";
import { cookieFlushPing as flushLoginCookies, finalizeLoginFlow, } from "../utils/loginFlow";
import { clearOtpState as clearPersistedOtpState, loadOtpState as loadPersistedOtpState, readStoredCallMeState as readPersistedCallMeState, readStoredOtpStateForRestore as readPersistedOtpStateForRestore, saveOtpState as savePersistedOtpState, } from "../utils/otpState";
import { hasNativeClipboardBridge, hasNativeDialerBridge, isInWebView, postToNative, supportsNativeLoginRedirect, } from "../utils/nativeBridge";
function readEnv(name) {
    try {
        const value = process.env?.[name];
        return typeof value === "string" ? value : "";
    }
    catch {
        return "";
    }
}
const API_BASE = readEnv("REACT_APP_ID_API_BASE") || readEnv("REACT_APP_IDP_ORIGIN") || "";
const APP_FALLBACK_HOME = readEnv("REACT_APP_CLIENT_HOME_URL") || "";
const CAPTCHA_SITEKEY = readEnv("REACT_APP_YANDEX_SMART_CAPTCHA_SITEKEY") || "ysc1_hCchgzfJU2rAYnCyEwloY0SmIXJvlO4Af01SbNtqe539d707";
const FALLBACK_PRIVACY_POLICY_URL = "https://chio-chio.ru/privacy-policy";
const FALLBACK_PERSONAL_DATA_CONSENT_URL = "https://chio-chio.ru/auth-personal-data-consent";
const LEGAL_CONSENT_ERROR_MESSAGE = "Подтвердите согласие на обработку персональных данных";
const LOGIN_DEBUG = readEnv("REACT_APP_LOGIN_DEBUG") === "1";
const OTP_MIN_LENGTH = 4;
const OTP_MAX_LENGTH = 6;
const PHONE_DIGITS_LENGTH = 11;
const CALL_ME_POLL_INTERVAL_MS = 2500;
const CALL_ME_DIALER_DELAY_MS = 300;
const CALL_ME_NATIVE_RESUME_EVENT = "cc_native_app_resumed";
/**
 * Если запрошен код через звонок/телеграм и пользователь не получил код —
 * через сколько секунд показать "Не пришел код?".
 */
const FALLBACK_SMS_AFTER_SEC = 7;
const FP_KEY = "csrf_fp";
const OTP_STATE_KEY = "otp_state_v1";
const FP_COOKIE_KEY = "cc_fp";
const OTP_STATE_COOKIE_KEY = "cc_otp_state_v1";
const FP_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 180;
function resolveApiBase() {
    const fallbackOrigin = typeof window !== "undefined" ? window.location.origin : "";
    return (API_BASE || fallbackOrigin).replace(/\/$/, "");
}
const DEFAULT_LEGAL_CONSENT_STATUS = {
    accepted: false,
    required: true,
    type: "personal_data_consent",
    documentVersion: "",
    documentKey: "auth_personal_data_consent",
    documentUrl: FALLBACK_PERSONAL_DATA_CONSENT_URL,
    privacyPolicyUrl: FALLBACK_PRIVACY_POLICY_URL,
    userAgreementUrl: null,
    source: "id_login_phone_step",
};
function buildCookieAttributes(maxAgeSeconds) {
    const attrs = [`Path=/`, `Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}`, "SameSite=Lax"];
    if (typeof window !== "undefined" && window.location.protocol === "https:") {
        attrs.push("Secure");
    }
    return attrs.join("; ");
}
function writeCookie(name, value, maxAgeSeconds) {
    if (typeof document === "undefined") {
        return;
    }
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ${buildCookieAttributes(maxAgeSeconds)}`;
}
function readCookie(name) {
    if (typeof document === "undefined") {
        return null;
    }
    const encodedName = `${encodeURIComponent(name)}=`;
    const parts = document.cookie ? document.cookie.split(/;\s*/) : [];
    for (const part of parts) {
        if (part.startsWith(encodedName)) {
            return decodeURIComponent(part.slice(encodedName.length));
        }
    }
    return null;
}
function clearCookie(name) {
    writeCookie(name, "", 0);
}
function writeCallMeStateToUrl(state) {
    if (typeof window === "undefined") {
        return;
    }
    try {
        const url = new URL(window.location.href);
        url.searchParams.set("callMeAttemptId", state.attemptId);
        url.searchParams.set("callMeRoutingKey", state.routingKey);
        url.searchParams.set("callMePhone", state.phone);
        url.searchParams.set("callMeExpiresAt", String(state.expiresAt));
        url.searchParams.set("callMeConsentAccepted", state.personalDataConsentAccepted ? "1" : "0");
        window.history.replaceState(window.history.state, "", url.toString());
    }
    catch { }
}
function clearCallMeStateFromUrl() {
    if (typeof window === "undefined") {
        return;
    }
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete("callMeAttemptId");
        url.searchParams.delete("callMeRoutingKey");
        url.searchParams.delete("callMePhone");
        url.searchParams.delete("callMeExpiresAt");
        url.searchParams.delete("callMeConsentAccepted");
        window.history.replaceState(window.history.state, "", url.toString());
    }
    catch { }
}
function readCallMeStateFromUrl() {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const url = new URL(window.location.href);
        const attemptId = url.searchParams.get("callMeAttemptId")?.trim() || "";
        const routingKey = url.searchParams.get("callMeRoutingKey")?.trim() || "";
        const phone = url.searchParams.get("callMePhone")?.trim() || "";
        const expiresAt = Number(url.searchParams.get("callMeExpiresAt") || "0");
        const personalDataConsentAccepted = url.searchParams.get("callMeConsentAccepted") === "1";
        if (!attemptId || !routingKey || !phone || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
            if (attemptId || routingKey || phone || expiresAt) {
                clearCallMeStateFromUrl();
            }
            return null;
        }
        return {
            attemptId,
            routingKey,
            phone,
            expiresAt,
            personalDataConsentAccepted,
        };
    }
    catch {
        return null;
    }
}
function getOrCreateFingerprint() {
    try {
        let value = localStorage.getItem(FP_KEY);
        if (!value) {
            value = readCookie(FP_COOKIE_KEY);
            if (value) {
                localStorage.setItem(FP_KEY, value);
            }
        }
        if (!value) {
            const nav = window.navigator;
            const parts = [
                nav.userAgent,
                nav.language,
                String(window.screen.width),
                String(window.screen.height),
                String(window.screen.colorDepth),
                Intl.DateTimeFormat().resolvedOptions().timeZone || "",
            ].join("|");
            const buf = new TextEncoder().encode(parts);
            const ascii = String.fromCharCode(...buf);
            value = btoa(ascii)
                .replace(/\+/g, "-")
                .replace(/\//g, "_")
                .replace(/=+$/, "");
            value += "." + Math.random().toString(36).slice(2, 10);
            localStorage.setItem(FP_KEY, value);
        }
        writeCookie(FP_COOKIE_KEY, value, FP_COOKIE_MAX_AGE_SEC);
        return value;
    }
    catch {
        const value = Math.random().toString(36).slice(2) + Date.now().toString(36);
        try {
            localStorage.setItem(FP_KEY, value);
        }
        catch { }
        writeCookie(FP_COOKIE_KEY, value, FP_COOKIE_MAX_AGE_SEC);
        return value;
    }
}
function normalizePhoneDigits(value) {
    let digits = value.replace(/\D/g, "");
    if (!digits)
        return "";
    if (digits.length === 10 && digits.startsWith("9")) {
        digits = `7${digits}`;
    }
    else if (digits.startsWith("8")) {
        digits = `7${digits.slice(1)}`;
    }
    return digits.slice(0, PHONE_DIGITS_LENGTH);
}
function formatPhoneInput(value) {
    const digits = normalizePhoneDigits(value);
    if (!digits)
        return "";
    const country = digits.slice(0, 1);
    const p1 = digits.slice(1, 4);
    const p2 = digits.slice(4, 7);
    const p3 = digits.slice(7, 9);
    const p4 = digits.slice(9, 11);
    let formatted = `+${country}`;
    if (p1)
        formatted += ` ${p1}`;
    if (p2)
        formatted += ` ${p2}`;
    if (p3)
        formatted += `-${p3}`;
    if (p4)
        formatted += `-${p4}`;
    return formatted;
}
function normalizePhoneForApi(value) {
    const digits = normalizePhoneDigits(value);
    return digits.length === PHONE_DIGITS_LENGTH ? `+${digits}` : value.trim();
}
function normalizeConsentLookupValue(value) {
    const trimmed = value.trim();
    const phoneLike = /^[+\d\s()\-\u00A0]+$/.test(trimmed);
    if (phoneLike) {
        const digits = normalizePhoneDigits(trimmed);
        if (digits.length >= 6) {
            return `+${digits}`;
        }
    }
    return trimmed.toLowerCase();
}
function isReadyPhoneForConsentLookup(value) {
    return normalizePhoneDigits(value).length === PHONE_DIGITS_LENGTH;
}
function isReadyIdentifierForConsentLookup(value) {
    return normalizeConsentLookupValue(value).length >= 6;
}
function isValidPhone(value) {
    return normalizePhoneDigits(value).length === PHONE_DIGITS_LENGTH;
}
function getOtpCodeLengthForChannel(channel) {
    if (channel === "call_me") {
        return 0;
    }
    return channel === "call" ? OTP_MIN_LENGTH : OTP_MAX_LENGTH;
}
function normalizeOtpCodeLength(value, channel) {
    if (channel === "call_me") {
        return 0;
    }
    if (typeof value !== "number" || !Number.isInteger(value)) {
        return getOtpCodeLengthForChannel(channel);
    }
    if (value < OTP_MIN_LENGTH || value > OTP_MAX_LENGTH) {
        return getOtpCodeLengthForChannel(channel);
    }
    return value;
}
function formatDigitsLabel(value) {
    const mod10 = value % 10;
    const mod100 = value % 100;
    if (mod10 === 1 && mod100 !== 11) {
        return `${value} цифра`;
    }
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return `${value} цифры`;
    }
    return `${value} цифр`;
}
function getOtpStateDeps() {
    return {
        storage: localStorage,
        otpStateKey: OTP_STATE_KEY,
        otpStateCookieKey: OTP_STATE_COOKIE_KEY,
        writeCookie,
        readCookie,
        clearCookie,
        writeCallMeStateToUrl,
        clearCallMeStateFromUrl,
        readCallMeStateFromUrl,
    };
}
function saveOtpState(phone, channel, ttlSeconds, personalDataConsentAccepted, codeLength, extras = {}) {
    savePersistedOtpState(getOtpStateDeps(), {
        phone,
        channel,
        ttlSeconds,
        personalDataConsentAccepted,
        codeLength,
        extras,
    });
}
function loadOtpState() {
    return loadPersistedOtpState(getOtpStateDeps());
}
function clearOtpState() {
    clearPersistedOtpState(getOtpStateDeps());
}
function readStoredCallMeState(expectedPhone) {
    return readPersistedCallMeState(getOtpStateDeps(), normalizePhoneForApi, expectedPhone);
}
function readStoredOtpStateForRestore() {
    return readPersistedOtpStateForRestore(getOtpStateDeps());
}
function readLegalConsentStatus(payload) {
    const source = payload?.legal?.personalDataConsent ?? payload?.personalDataConsent ?? null;
    if (!source || typeof source !== "object") {
        return null;
    }
    return {
        accepted: Boolean(source.accepted),
        required: Boolean(source.required ?? !source.accepted),
        type: typeof source.type === "string" ? source.type : DEFAULT_LEGAL_CONSENT_STATUS.type,
        documentVersion: typeof source.documentVersion === "string" ? source.documentVersion : DEFAULT_LEGAL_CONSENT_STATUS.documentVersion,
        documentKey: typeof source.documentKey === "string" ? source.documentKey : DEFAULT_LEGAL_CONSENT_STATUS.documentKey,
        documentUrl: typeof source.documentUrl === "string" ? source.documentUrl : DEFAULT_LEGAL_CONSENT_STATUS.documentUrl,
        privacyPolicyUrl: typeof source.privacyPolicyUrl === "string" ? source.privacyPolicyUrl : DEFAULT_LEGAL_CONSENT_STATUS.privacyPolicyUrl,
        userAgreementUrl: typeof source.userAgreementUrl === "string" ? source.userAgreementUrl : null,
        source: typeof source.source === "string" ? source.source : DEFAULT_LEGAL_CONSENT_STATUS.source,
    };
}
function withClientHeaders(headers) {
    if (!isInWebView()) {
        return headers;
    }
    return {
        ...headers,
        "X-Client": "mobile",
    };
}
function getVersionFromDevice(device) {
    if (!device)
        return null;
    const value = device.expoVersion || device.versionApp || device.version || null;
    if (!value)
        return null;
    const trimmed = String(value).trim();
    return trimmed || null;
}
function mapError(code) {
    switch (code) {
        case "invalid_request":
            return "Неполные или неверные данные";
        case "invalid_credentials":
            return "Неверный логин или пароль";
        case "user_not_found":
            return "Пользователь не найден";
        case "too_often":
            return "Слишком часто. Повторите позже";
        case "too_many_requests":
            return "Слишком много попыток. Подождите и повторите";
        case "invalid_code":
            return "Неверный или просроченный код";
        case "call_me_not_ready":
            return "Звонок ещё не подтверждён. Завершите звонок и обновите статус.";
        case "call_me_timeout":
            return "Не получили подтверждение звонка вовремя. Попробуйте снова или выберите SMS.";
        case "channel_unavailable":
            return "Канал сейчас недоступен. Попробуйте другой способ входа.";
        case "already_finalized":
            return "Эта попытка входа уже завершена. Запросите новый вход при необходимости.";
        case "captcha_failed":
            return "Не удалось подтвердить, что вы не робот";
        case "legal_consent_required":
            return LEGAL_CONSENT_ERROR_MESSAGE;
        default:
            return "Ошибка. Попробуйте ещё раз";
    }
}
function labelChannel(channel) {
    if (channel === "call_me")
        return "Позвоню сам";
    if (channel === "call")
        return "входящий звонок";
    if (channel === "telegram")
        return "Telegram";
    if (channel === "whatsapp")
        return "WhatsApp";
    return "SMS";
}
function getChannelDescription(channel) {
    switch (channel) {
        case "call":
            return "Мы звоним вам, а вы вводите код из входящего звонка.";
        case "call_me":
            return "Вы звоните сами на выделенный номер, вход подтверждается автоматически.";
        case "telegram":
            return "Код приходит сообщением в Telegram.";
        case "whatsapp":
            return "Код приходит сообщением в WhatsApp.";
        default:
            return "Код приходит сообщением на телефон.";
    }
}
function getChannelSelectionNote(channel) {
    switch (channel) {
        case "call":
            return {
                title: "Код звонком: мы звоним вам",
                text: "Иногда входящий звонок не доходит, если на телефоне срабатывает антиспам или блокировка неизвестных номеров. Если звонка нет, можно выбрать «Позвоню сам» или перейти на SMS.",
            };
        case "call_me":
            return {
                title: "Позвоню сам: вы звоните сами",
                text: "На следующем шаге мы покажем номер, на который нужно позвонить со своего телефона. Этот способ обычно удобнее, если входящие звонки блокируются антиспамом.",
            };
        case "telegram":
            return {
                title: "Telegram: код в сообщении",
                text: "Сообщение может не прийти, если Telegram недоступен, работает нестабильно или блокируется в вашей сети. В этом случае можно выбрать «Позвоню сам» или SMS.",
            };
        case "whatsapp":
            return {
                title: "WhatsApp: код в сообщении",
                text: "Если сообщение не приходит, используйте другой способ подтверждения.",
            };
        default:
            return {
                title: "SMS: код в сообщении",
                text: "Если сообщение задерживается, попробуйте другой способ подтверждения.",
            };
    }
}
function getVerifyChannelHelp(channel) {
    switch (channel) {
        case "call":
            return "Если входящего звонка нет, причина может быть в антиспаме или блокировке неизвестных номеров на телефоне. Тогда откройте «Не пришел код?» и переключитесь на SMS или вернитесь и выберите «Позвоню сам».";
        case "telegram":
            return "Если сообщение в Telegram не приходит, Telegram может быть недоступен или блокироваться в вашей сети. Тогда используйте SMS или вернитесь и выберите «Позвоню сам».";
        default:
            return "";
    }
}
function normalizeCallMeAttemptStatus(value) {
    if (typeof value !== "string") {
        return "pending";
    }
    switch (value) {
        case "authorized":
            return "authorized";
        case "finalized":
            return "authorized";
        case "expired":
            return "expired";
        case "finalizing":
            return "finalizing";
        default:
            return "pending";
    }
}
function buildCallHref(value) {
    const digits = value.replace(/\D/g, "");
    return digits ? `tel:+${digits}` : "#";
}
async function copyTextToClipboard(value) {
    const text = value.trim();
    if (!text) {
        return false;
    }
    let copied = false;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            copied = true;
        }
        catch { }
    }
    if (!copied && typeof document !== "undefined") {
        try {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.setAttribute("readonly", "true");
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            textarea.style.pointerEvents = "none";
            document.body.appendChild(textarea);
            textarea.select();
            textarea.setSelectionRange(0, text.length);
            copied = document.execCommand("copy");
            document.body.removeChild(textarea);
        }
        catch { }
    }
    if (isInWebView() && hasNativeClipboardBridge()) {
        postToNative("copy_to_clipboard", { text });
        return true;
    }
    return copied;
}
function tryOpenDialer(phone) {
    if (typeof window === "undefined") {
        return;
    }
    const href = buildCallHref(phone);
    if (href === "#") {
        return;
    }
    if (isInWebView()) {
        postToNative("open_phone_dialer", {
            phone: normalizePhoneForApi(phone),
            url: href,
        });
        if (hasNativeDialerBridge()) {
            return;
        }
    }
    try {
        window.location.href = href;
    }
    catch { }
}
function maskPhone(value) {
    const formatted = formatPhoneInput(value) || value;
    const digits = formatted.replace(/\D+/g, "");
    if (digits.length < 6)
        return formatted;
    return formatted.slice(0, -6).replace(/\d/g, "•") + formatted.slice(-6);
}
function useNativeDeviceInfo() {
    const [expoPushToken, setExpoPushToken] = useState(null);
    const [nativeDevice, setNativeDevice] = useState(null);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        if (!isInWebView()) {
            return;
        }
        function tokenHandler(ev) {
            try {
                const anyEv = ev;
                const token = anyEv?.detail;
                if (typeof token === "string" && token) {
                    setExpoPushToken(token);
                    try {
                        localStorage.setItem("expo_push_token", token);
                    }
                    catch { }
                }
            }
            catch { }
        }
        function deviceHandler(ev) {
            try {
                const anyEv = ev;
                const data = anyEv?.detail;
                if (data && typeof data === "object") {
                    setNativeDevice(data);
                    const token = data.expoPushToken;
                    if (typeof token === "string" && token) {
                        setExpoPushToken(token);
                        try {
                            localStorage.setItem("expo_push_token", token);
                        }
                        catch { }
                    }
                    try {
                        localStorage.setItem("expo_device_info", JSON.stringify(data));
                    }
                    catch { }
                }
            }
            catch { }
        }
        window.addEventListener("expoPushToken", tokenHandler);
        window.addEventListener("expoDeviceInfo", deviceHandler);
        const existingDevice = window.__EXPO_DEVICE_INFO__;
        if (existingDevice && typeof existingDevice === "object") {
            setNativeDevice(existingDevice);
            if (typeof existingDevice.expoPushToken === "string" && existingDevice.expoPushToken) {
                setExpoPushToken(existingDevice.expoPushToken);
            }
        }
        else {
            try {
                const raw = localStorage.getItem("expo_device_info");
                if (raw) {
                    const parsed = JSON.parse(raw);
                    setNativeDevice(parsed);
                    if (typeof parsed.expoPushToken === "string" && parsed.expoPushToken) {
                        setExpoPushToken(parsed.expoPushToken);
                    }
                }
            }
            catch { }
        }
        const existingToken = window.__EXPO_PUSH_TOKEN__;
        if (typeof existingToken === "string" && existingToken) {
            setExpoPushToken(existingToken);
        }
        return () => {
            window.removeEventListener("expoPushToken", tokenHandler);
            window.removeEventListener("expoDeviceInfo", deviceHandler);
        };
    }, []);
    return {
        expoPushToken,
        nativeDevice,
    };
}
export default function LoginPage() {
    const [authMode, setAuthMode] = useState("otp");
    const [step, setStep] = useState("request");
    const [phone, setPhone] = useState("");
    const [channel, setChannel] = useState("call");
    const [otpSent, setOtpSent] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [otpCodeLength, setOtpCodeLength] = useState(() => getOtpCodeLengthForChannel("call"));
    const [otpTTL, setOtpTTL] = useState(0);
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [personalDataConsentChecked, setPersonalDataConsentChecked] = useState(false);
    const [legalStatus, setLegalStatus] = useState(DEFAULT_LEGAL_CONSENT_STATUS);
    const [phoneConsentLookupKey, setPhoneConsentLookupKey] = useState("");
    const [phoneConsentAccepted, setPhoneConsentAccepted] = useState(false);
    const [identifierConsentLookupKey, setIdentifierConsentLookupKey] = useState("");
    const [identifierConsentAccepted, setIdentifierConsentAccepted] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");
    const [captchaVersion, setCaptchaVersion] = useState(0);
    const [fingerprint] = useState(() => getOrCreateFingerprint());
    const { expoPushToken, nativeDevice } = useNativeDeviceInfo();
    const [showNoCodeBtn, setShowNoCodeBtn] = useState(false);
    const [showSendSmsBtn, setShowSendSmsBtn] = useState(false);
    const fallbackTimerRef = useRef(null);
    const consentStatusCacheRef = useRef(new Map());
    const [callMeAttemptId, setCallMeAttemptId] = useState("");
    const [callMeRoutingKey, setCallMeRoutingKey] = useState("");
    const [callMeStatus, setCallMeStatus] = useState("idle");
    const [verifyCaptchaAction, setVerifyCaptchaAction] = useState(null);
    const callMePollInFlightRef = useRef(false);
    const callMeFinalizeStartedRef = useRef(false);
    const [showDeviceDebug, setShowDeviceDebug] = useState(false);
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [codeTouched, setCodeTouched] = useState(false);
    const [identifierTouched, setIdentifierTouched] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [consentTouched, setConsentTouched] = useState(false);
    const phoneInputRef = useRef(null);
    const codeInputRef = useRef(null);
    const identifierInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const consentCheckboxRef = useRef(null);
    const silentNativeRestoreStartedRef = useRef(false);
    useEffect(() => {
        document.documentElement.style.setProperty("--brand-red", "#e72f44");
        document.documentElement.style.setProperty("--brand-navy", "#0d263f");
    }, []);
    useEffect(() => {
        const stored = readStoredOtpStateForRestore();
        if (!stored)
            return;
        if (stored.channel === "call_me" && !stored.attemptId) {
            clearOtpState();
            return;
        }
        const remaining = Math.ceil((stored.expiresAt - Date.now()) / 1000);
        if (remaining <= 0) {
            clearOtpState();
            return;
        }
        setAuthMode("otp");
        setPhone(formatPhoneInput(stored.phone));
        setChannel(stored.channel);
        setOtpCodeLength(normalizeOtpCodeLength(stored.codeLength, stored.channel));
        setStep("verify");
        setOtpSent(true);
        setOtpTTL(remaining);
        setPersonalDataConsentChecked(Boolean(stored.personalDataConsentAccepted));
        setCallMeAttemptId(stored.attemptId ?? "");
        setCallMeRoutingKey(stored.routingKey ?? "");
        setCallMeStatus(stored.channel === "call_me" ? "pending" : "idle");
    }, []);
    useEffect(() => {
        if (!otpSent || otpTTL <= 0)
            return;
        const intervalId = window.setInterval(() => {
            setOtpTTL((ttl) => {
                if (ttl <= 1) {
                    clearOtpState();
                    return 0;
                }
                return ttl - 1;
            });
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [otpSent, otpTTL]);
    useEffect(() => {
        return () => {
            if (fallbackTimerRef.current) {
                window.clearTimeout(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
            }
        };
    }, []);
    const fallbackEligible = authMode === "otp" && step === "verify" && otpTTL > 0 && (channel === "call" || channel === "telegram" || channel === "call_me");
    useEffect(() => {
        if (authMode !== "otp" ||
            step !== "verify" ||
            channel !== "call_me" ||
            (callMeAttemptId === "" && !isValidPhone(phone)) ||
            callMeStatus !== "pending" ||
            otpTTL <= 0) {
            return;
        }
        void pollCallMeStatus();
        const intervalId = window.setInterval(() => {
            void pollCallMeStatus();
        }, CALL_ME_POLL_INTERVAL_MS);
        return () => {
            window.clearInterval(intervalId);
        };
    }, [authMode, step, channel, callMeAttemptId, callMeStatus, otpTTL]);
    useEffect(() => {
        if (authMode === "otp" &&
            step === "verify" &&
            channel === "call_me" &&
            callMeAttemptId !== "" &&
            callMeStatus === "authorized") {
            void finalizeCallMe();
        }
    }, [authMode, step, channel, callMeAttemptId, callMeStatus]);
    useEffect(() => {
        if (authMode === "otp" &&
            step === "verify" &&
            channel === "call_me" &&
            callMeAttemptId !== "" &&
            otpTTL <= 0 &&
            callMeStatus === "pending") {
            setCallMeStatus("expired");
            setShowSendSmsBtn(true);
        }
    }, [authMode, step, channel, callMeAttemptId, otpTTL, callMeStatus]);
    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }
        const resumeCallMeFlow = () => {
            if (authMode !== "otp" || step !== "verify" || channel !== "call_me") {
                return;
            }
            const restored = callMeAttemptId ? null : hydrateCallMeStateFromStorage();
            const effectiveAttemptId = callMeAttemptId || restored?.attemptId || "";
            if (!effectiveAttemptId) {
                return;
            }
            // Android WebView can suspend an in-flight status/finalize request while the phone app is open.
            // When the user returns, force a fresh check instead of waiting on a stale promise forever.
            callMePollInFlightRef.current = false;
            if (callMeStatus === "finalizing") {
                callMeFinalizeStartedRef.current = false;
                setLoading(false);
                setCallMeStatus("authorized");
                return;
            }
            if (callMeStatus === "authorized") {
                callMeFinalizeStartedRef.current = false;
                setLoading(false);
                void finalizeCallMe();
                return;
            }
            if (callMeStatus === "pending" && otpTTL > 0) {
                void pollCallMeStatus();
            }
        };
        const handleWindowFocus = () => {
            resumeCallMeFlow();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                resumeCallMeFlow();
            }
        };
        window.addEventListener("focus", handleWindowFocus);
        window.addEventListener("pageshow", handleWindowFocus);
        window.addEventListener(CALL_ME_NATIVE_RESUME_EVENT, handleWindowFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            window.removeEventListener("focus", handleWindowFocus);
            window.removeEventListener("pageshow", handleWindowFocus);
            window.removeEventListener(CALL_ME_NATIVE_RESUME_EVENT, handleWindowFocus);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [authMode, step, channel, callMeAttemptId, callMeStatus, otpTTL]);
    useEffect(() => {
        if (!fallbackEligible) {
            setShowNoCodeBtn(false);
            setShowSendSmsBtn(false);
            if (fallbackTimerRef.current) {
                window.clearTimeout(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
            }
            return;
        }
        if (showNoCodeBtn || showSendSmsBtn) {
            return;
        }
        if (fallbackTimerRef.current) {
            window.clearTimeout(fallbackTimerRef.current);
        }
        fallbackTimerRef.current = window.setTimeout(() => {
            setShowNoCodeBtn(true);
        }, FALLBACK_SMS_AFTER_SEC * 1000);
        return () => {
            if (fallbackTimerRef.current) {
                window.clearTimeout(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
            }
        };
    }, [fallbackEligible, showNoCodeBtn, showSendSmsBtn]);
    function resetFallbackUi() {
        setShowNoCodeBtn(false);
        setShowSendSmsBtn(false);
        setVerifyCaptchaAction(null);
        if (fallbackTimerRef.current) {
            window.clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
    }
    function resetCallMeState() {
        callMePollInFlightRef.current = false;
        callMeFinalizeStartedRef.current = false;
        setCallMeAttemptId("");
        setCallMeRoutingKey("");
        setCallMeStatus("idle");
    }
    function resetOtpFlowToRequest() {
        clearOtpState();
        resetFallbackUi();
        resetCallMeState();
        setVerifyCaptchaAction(null);
        setStep("request");
        setOtpSent(false);
        setOtpTTL(0);
        setOtpCode("");
        setOtpCodeLength(getOtpCodeLengthForChannel("call"));
        setCodeTouched(false);
        setPhoneTouched(false);
        resetCaptcha();
    }
    function resetCaptcha() {
        setCaptchaToken("");
        setCaptchaVersion((prev) => prev + 1);
    }
    function clearFeedback() {
        setError("");
        setSuccess("");
    }
    function focusConsent() {
        consentCheckboxRef.current?.focus();
    }
    function applyMappedError(code) {
        setError(mapError(code));
        if (code === "captcha_failed") {
            resetCaptcha();
        }
        if (code === "legal_consent_required") {
            setConsentTouched(true);
            focusConsent();
        }
    }
    function handleCaptchaSuccess(token) {
        setCaptchaToken(token);
        setError((prev) => (prev.includes("Я не робот") ? "" : prev));
        if (verifyCaptchaAction) {
            void requestOtp(verifyCaptchaAction.channel, {
                force: verifyCaptchaAction.force,
                fromFallback: verifyCaptchaAction.fromFallback,
                captchaTokenOverride: token,
            });
        }
    }
    function buildVerifyCaptchaMessage(action) {
        if (action?.fromFallback || action?.channel === "sms") {
            return "Чтобы отправить SMS, пройдите проверку «Я не робот» ещё раз. Это отдельный запрос.";
        }
        return "Чтобы отправить код повторно, пройдите проверку «Я не робот» ещё раз.";
    }
    function mergeLegalStatus(next) {
        if (!next)
            return;
        setLegalStatus((prev) => ({
            ...prev,
            ...next,
            documentUrl: next.documentUrl || prev.documentUrl,
            privacyPolicyUrl: next.privacyPolicyUrl || prev.privacyPolicyUrl,
            userAgreementUrl: next.userAgreementUrl ?? prev.userAgreementUrl,
        }));
    }
    function applyConsentLookupResult(scope, lookupKey, accepted) {
        if (scope === "phone") {
            setPhoneConsentLookupKey(lookupKey);
            setPhoneConsentAccepted(accepted);
            return;
        }
        setIdentifierConsentLookupKey(lookupKey);
        setIdentifierConsentAccepted(accepted);
    }
    async function syncPersonalDataConsentStatus(rawIdentifier, scope) {
        const lookupKey = normalizeConsentLookupValue(rawIdentifier);
        const lookupReady = scope === "phone"
            ? isReadyPhoneForConsentLookup(rawIdentifier)
            : isReadyIdentifierForConsentLookup(rawIdentifier);
        if (!lookupReady || lookupKey === "") {
            applyConsentLookupResult(scope, "", false);
            return false;
        }
        const cached = consentStatusCacheRef.current.get(lookupKey);
        if (cached) {
            mergeLegalStatus(cached);
            applyConsentLookupResult(scope, lookupKey, Boolean(cached.accepted));
            return Boolean(cached.accepted);
        }
        try {
            const url = new URL(`${resolveApiBase()}/api/legal/personal-data-consent-status`);
            url.searchParams.set("identifier", rawIdentifier.trim());
            const res = await fetch(url.toString(), {
                method: "GET",
                credentials: "include",
                cache: "no-store",
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) {
                applyConsentLookupResult(scope, lookupKey, false);
                return false;
            }
            const parsed = readLegalConsentStatus(data);
            if (!parsed) {
                applyConsentLookupResult(scope, lookupKey, false);
                return false;
            }
            const resolved = {
                ...DEFAULT_LEGAL_CONSENT_STATUS,
                ...legalStatus,
                ...parsed,
            };
            consentStatusCacheRef.current.set(lookupKey, resolved);
            mergeLegalStatus(parsed);
            applyConsentLookupResult(scope, lookupKey, Boolean(parsed.accepted));
            return Boolean(parsed.accepted);
        }
        catch {
            applyConsentLookupResult(scope, lookupKey, false);
            return false;
        }
    }
    function rememberAcceptedConsent(rawIdentifier) {
        const lookupKey = normalizeConsentLookupValue(rawIdentifier);
        if (!lookupKey)
            return;
        const acceptedStatus = {
            ...legalStatus,
            accepted: true,
            required: false,
        };
        consentStatusCacheRef.current.set(lookupKey, acceptedStatus);
    }
    function buildDevicePayload() {
        return {
            expoPushToken: expoPushToken ?? nativeDevice?.expoPushToken ?? null,
            fcmToken: nativeDevice?.fcmToken ?? null,
            voipPushToken: nativeDevice?.voipPushToken ?? null,
            nativeCallCapable: nativeDevice?.nativeCallCapable ?? false,
            callKitEnabled: nativeDevice?.callKitEnabled ?? false,
            os: nativeDevice?.os ?? null,
            deviceInfo: nativeDevice?.deviceInfo ?? null,
            modelId: nativeDevice?.modelId ?? null,
            modelName: nativeDevice?.modelName ?? null,
            osVersion: nativeDevice?.osVersion ?? null,
            expoVersion: nativeDevice?.expoVersion ?? null,
            version: nativeDevice?.version ?? null,
            versionApp: nativeDevice?.versionApp ?? null,
            applicationId: nativeDevice?.applicationId ?? null,
        };
    }
    async function finalizeLogin(access_token, refresh_token) {
        await finalizeLoginFlow({
            accessToken: access_token,
            refreshToken: refresh_token,
            fingerprint,
            appFallbackHome: APP_FALLBACK_HOME,
            isNativeWebView: isInWebView(),
            supportsNativeLoginRedirect: supportsNativeLoginRedirect(),
            location: window.location,
            sessionStorage,
            postToNative,
            flushCookies: (fp) => flushLoginCookies({
                fingerprint: fp,
                apiBase: resolveApiBase(),
                isInWebView: isInWebView(),
            }),
            assignHref: (target) => {
                window.location.href = target;
            },
        });
    }
    useEffect(() => {
        if (!isInWebView() || silentNativeRestoreStartedRef.current) {
            return;
        }
        silentNativeRestoreStartedRef.current = true;
        let cancelled = false;
        (async () => {
            const exported = await flushLoginCookies({
                fingerprint,
                apiBase: resolveApiBase(),
                isInWebView: true,
                firstDelayMs: 0,
                secondDelayMs: 0,
            });
            if (cancelled || !exported.refreshToken) {
                return;
            }
            await finalizeLoginFlow({
                accessToken: "",
                refreshToken: exported.refreshToken,
                fingerprint: exported.fingerprint || fingerprint,
                appFallbackHome: APP_FALLBACK_HOME,
                isNativeWebView: true,
                supportsNativeLoginRedirect: supportsNativeLoginRedirect(),
                location: window.location,
                sessionStorage,
                postToNative,
                flushCookies: async () => exported,
                assignHref: (target) => {
                    window.location.href = target;
                },
            });
        })().catch(() => {
            // Stay on the regular login form when silent restore is unavailable.
        });
        return () => {
            cancelled = true;
        };
    }, [fingerprint]);
    function handlePhoneChange(nextValue) {
        const formatted = formatPhoneInput(nextValue);
        setPhone(formatted);
        if (normalizeConsentLookupValue(formatted) !== phoneConsentLookupKey) {
            setPhoneConsentLookupKey("");
            setPhoneConsentAccepted(false);
        }
    }
    function handleIdentifierChange(nextValue) {
        setIdentifier(nextValue);
        if (normalizeConsentLookupValue(nextValue) !== identifierConsentLookupKey) {
            setIdentifierConsentLookupKey("");
            setIdentifierConsentAccepted(false);
        }
    }
    function handlePersonalDataConsentChange(nextChecked) {
        setPersonalDataConsentChecked(nextChecked);
        setConsentTouched(true);
        setError((prev) => (prev === LEGAL_CONSENT_ERROR_MESSAGE ? "" : prev));
    }
    function handleAuthModeChange(nextMode) {
        setAuthMode(nextMode);
        clearFeedback();
        setVerifyCaptchaAction(null);
        setPhoneTouched(false);
        setCodeTouched(false);
        setIdentifierTouched(false);
        setPasswordTouched(false);
        setConsentTouched(false);
        if (nextMode === "password") {
            resetFallbackUi();
            resetCallMeState();
        }
    }
    function hydrateCallMeStateFromStorage() {
        const restored = readStoredCallMeState(phone);
        if (!restored) {
            return null;
        }
        if (!callMeAttemptId && restored.attemptId) {
            setCallMeAttemptId(restored.attemptId);
        }
        if (!callMeRoutingKey && restored.routingKey) {
            setCallMeRoutingKey(restored.routingKey);
        }
        if (!otpSent) {
            setOtpSent(true);
        }
        if (step !== "verify") {
            setStep("verify");
        }
        return restored;
    }
    async function pollCallMeStatus(opts) {
        const restored = callMeAttemptId ? null : hydrateCallMeStateFromStorage();
        const effectiveAttemptId = callMeAttemptId || restored?.attemptId || "";
        const normalizedPhone = normalizePhoneForApi(phone);
        if ((!effectiveAttemptId && !normalizedPhone) || callMePollInFlightRef.current) {
            return;
        }
        callMePollInFlightRef.current = true;
        try {
            const url = new URL(`${resolveApiBase()}/api/otp/call-me/status`);
            if (effectiveAttemptId) {
                url.searchParams.set("id", effectiveAttemptId);
            }
            else {
                url.searchParams.set("phone", normalizedPhone);
            }
            const res = await fetch(url.toString(), {
                method: "GET",
                headers: withClientHeaders({
                    "X-CSRF-Token": fingerprint,
                    "Cache-Control": "no-store",
                }),
                credentials: "include",
                cache: "no-store",
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok)
                throw new Error(data?.error || "otp_error");
            const nextStatus = normalizeCallMeAttemptStatus(data?.status);
            const nextTtl = Math.max(0, Number(data?.ttl ?? otpTTL));
            const nextAttemptId = typeof data?.attempt_id === "string" && data.attempt_id.trim() !== ""
                ? String(data.attempt_id)
                : effectiveAttemptId;
            const nextRoutingKey = typeof data?.routing_key === "string" && data.routing_key.trim() !== ""
                ? String(data.routing_key)
                : callMeRoutingKey;
            setOtpTTL(nextTtl);
            if (nextAttemptId && nextAttemptId !== callMeAttemptId) {
                setCallMeAttemptId(nextAttemptId);
            }
            setCallMeRoutingKey(nextRoutingKey);
            setCallMeStatus(nextStatus);
            saveOtpState(normalizedPhone, "call_me", nextTtl, personalDataConsentChecked || phoneConsentAccepted, 0, {
                attemptId: nextAttemptId,
                routingKey: nextRoutingKey,
            });
            if (nextStatus === "expired") {
                setShowSendSmsBtn(true);
                setError(mapError("call_me_timeout"));
            }
            else if (nextStatus === "authorized") {
                void finalizeCallMe();
            }
        }
        catch (err) {
            if (opts?.manual) {
                applyMappedError(err instanceof Error ? err.message : String(err));
            }
        }
        finally {
            callMePollInFlightRef.current = false;
        }
    }
    async function finalizeCallMe() {
        const restored = callMeAttemptId ? null : hydrateCallMeStateFromStorage();
        const effectiveAttemptId = callMeAttemptId || restored?.attemptId || "";
        if (!effectiveAttemptId || callMeFinalizeStartedRef.current) {
            return;
        }
        callMeFinalizeStartedRef.current = true;
        const normalizedPhone = normalizePhoneForApi(phone);
        try {
            const consentAccepted = (await syncPersonalDataConsentStatus(normalizedPhone, "phone")) || personalDataConsentChecked;
            if (!consentAccepted) {
                callMeFinalizeStartedRef.current = false;
                setError(LEGAL_CONSENT_ERROR_MESSAGE);
                focusConsent();
                return;
            }
            setLoading(true);
            setCallMeStatus("finalizing");
            const res = await fetch(`${resolveApiBase()}/api/otp/call-me/finalize`, {
                method: "POST",
                headers: withClientHeaders({
                    "Content-Type": "application/json",
                    "X-CSRF-Token": fingerprint,
                }),
                credentials: "include",
                body: JSON.stringify({
                    attempt_id: effectiveAttemptId,
                    fingerprint,
                    personalDataConsentAccepted: consentAccepted,
                    device: buildDevicePayload(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            mergeLegalStatus(readLegalConsentStatus(data));
            if (!res.ok) {
                const code = data?.error || "otp_error";
                if (code === "call_me_not_ready") {
                    callMeFinalizeStartedRef.current = false;
                    setCallMeStatus("pending");
                    return;
                }
                throw new Error(code);
            }
            rememberAcceptedConsent(normalizedPhone);
            clearOtpState();
            resetFallbackUi();
            resetCallMeState();
            await finalizeLogin(data.access_token, data.refresh_token);
        }
        catch (err) {
            callMeFinalizeStartedRef.current = false;
            setCallMeStatus("authorized");
            applyMappedError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setLoading(false);
        }
    }
    async function handleCopyCallMeRoutingKey() {
        const value = normalizePhoneForApi(callMeRoutingKey);
        if (!value) {
            setError("Не удалось скопировать номер");
            return;
        }
        clearFeedback();
        const copied = await copyTextToClipboard(value);
        if (copied) {
            setSuccess("Номер скопирован. Можно вставить его в приложение «Телефон».");
            return;
        }
        setError("Не удалось скопировать номер. Попробуйте ещё раз.");
    }
    function handleOpenCallMeDialer() {
        clearFeedback();
        tryOpenDialer(callMeRoutingKey);
    }
    async function requestOtp(ch, opts) {
        const force = Boolean(opts?.force);
        const normalizedPhone = normalizePhoneForApi(phone);
        const effectiveCaptchaToken = opts?.captchaTokenOverride ?? captchaToken;
        const nextAction = {
            channel: ch,
            force,
            fromFallback: Boolean(opts?.fromFallback),
        };
        const canAttemptServerSideCaptchaReuse = authMode === "otp" && step === "verify" && verifyCaptchaAction === null;
        clearFeedback();
        setPhoneTouched(true);
        setConsentTouched(true);
        if (!isValidPhone(phone)) {
            phoneInputRef.current?.focus();
            setError("Введите телефон в формате +7 999 123-45-67");
            return;
        }
        if (!effectiveCaptchaToken && !canAttemptServerSideCaptchaReuse) {
            if (authMode === "otp" && step === "verify") {
                setVerifyCaptchaAction(nextAction);
                setError(buildVerifyCaptchaMessage(nextAction));
            }
            else {
                setError("Подтвердите, что вы не робот");
            }
            return;
        }
        const consentAccepted = (await syncPersonalDataConsentStatus(normalizedPhone, "phone")) || personalDataConsentChecked;
        if (!consentAccepted) {
            setError(LEGAL_CONSENT_ERROR_MESSAGE);
            focusConsent();
            return;
        }
        if (otpTTL > 0 && !force)
            return;
        setLoading(true);
        try {
            setChannel(ch);
            if (opts?.fromFallback) {
                resetFallbackUi();
            }
            const apiBase = resolveApiBase();
            const endpoint = ch === "call_me" ? `${apiBase}/api/otp/call-me/request` : `${apiBase}/api/otp/request`;
            const payload = ch === "call_me"
                ? {
                    phone: normalizedPhone,
                    fingerprint,
                    captchaToken: effectiveCaptchaToken,
                }
                : {
                    phone: normalizedPhone,
                    channel: ch,
                    fingerprint,
                    captchaToken: effectiveCaptchaToken,
                };
            const res = await fetch(endpoint, {
                method: "POST",
                headers: withClientHeaders({
                    "Content-Type": "application/json",
                    "X-CSRF-Token": fingerprint,
                }),
                credentials: "include",
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok)
                throw new Error(data?.error || "otp_error");
            const ttl = Number(data?.ttl ?? 300);
            const nextCodeLength = normalizeOtpCodeLength(data?.code_length, ch);
            const nextAttemptId = typeof data?.attempt_id === "string" ? data.attempt_id : "";
            const nextRoutingKey = typeof data?.routing_key === "string" ? data.routing_key : "";
            setOtpSent(true);
            setOtpTTL(ttl);
            setOtpCodeLength(nextCodeLength);
            setSuccess(ch === "call_me"
                ? "Открываем звонок. Если этого не произошло, номер уже показан на следующем шаге."
                : ch === "sms"
                    ? "Код отправлен в SMS"
                    : "Код отправлен");
            setStep("verify");
            setCodeTouched(false);
            if (ch === "call_me") {
                setOtpCode("");
                callMePollInFlightRef.current = false;
                setCallMeAttemptId(nextAttemptId);
                setCallMeRoutingKey(nextRoutingKey);
                setCallMeStatus("pending");
                callMeFinalizeStartedRef.current = false;
                window.setTimeout(() => {
                    tryOpenDialer(nextRoutingKey);
                }, CALL_ME_DIALER_DELAY_MS);
            }
            else {
                setOtpCode(data?.debug_code ? String(data.debug_code) : "");
                resetCallMeState();
            }
            saveOtpState(normalizedPhone, ch, ttl, consentAccepted, nextCodeLength, {
                attemptId: ch === "call_me" ? nextAttemptId : undefined,
                routingKey: ch === "call_me" ? nextRoutingKey : undefined,
            });
            setVerifyCaptchaAction(null);
            resetCaptcha();
        }
        catch (err) {
            const code = err instanceof Error ? err.message : String(err);
            if (code === "captcha_failed" && authMode === "otp" && step === "verify") {
                setVerifyCaptchaAction(nextAction);
                applyMappedError(code);
                setError(buildVerifyCaptchaMessage(nextAction));
                return;
            }
            applyMappedError(code);
        }
        finally {
            setLoading(false);
        }
    }
    async function verifyOtp(e) {
        e.preventDefault();
        clearFeedback();
        setCodeTouched(true);
        setConsentTouched(true);
        const normalizedPhone = normalizePhoneForApi(phone);
        const code = otpCode.replace(/\D/g, "").slice(0, otpCodeLength);
        if (code.length !== otpCodeLength) {
            setError(`Введите ${formatDigitsLabel(otpCodeLength)}`);
            codeInputRef.current?.focus();
            return;
        }
        const consentAccepted = (await syncPersonalDataConsentStatus(normalizedPhone, "phone")) || personalDataConsentChecked;
        if (!consentAccepted) {
            setError(LEGAL_CONSENT_ERROR_MESSAGE);
            focusConsent();
            return;
        }
        setLoading(true);
        try {
            const apiBase = resolveApiBase();
            const res = await fetch(`${apiBase}/api/otp/verify`, {
                method: "POST",
                headers: withClientHeaders({
                    "Content-Type": "application/json",
                    "X-CSRF-Token": fingerprint,
                }),
                credentials: "include",
                body: JSON.stringify({
                    phone: normalizedPhone,
                    code,
                    fingerprint,
                    personalDataConsentAccepted: consentAccepted,
                    device: buildDevicePayload(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            mergeLegalStatus(readLegalConsentStatus(data));
            if (!res.ok)
                throw new Error(data?.error || "invalid_code");
            rememberAcceptedConsent(normalizedPhone);
            clearOtpState();
            resetFallbackUi();
            await finalizeLogin(data.access_token, data.refresh_token);
        }
        catch (err) {
            applyMappedError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setLoading(false);
        }
    }
    async function passwordLogin(e) {
        e.preventDefault();
        clearFeedback();
        setIdentifierTouched(true);
        setPasswordTouched(true);
        setConsentTouched(true);
        if (!identifier.trim()) {
            setError("Укажите email или телефон");
            identifierInputRef.current?.focus();
            return;
        }
        if (!password) {
            setError("Введите пароль");
            passwordInputRef.current?.focus();
            return;
        }
        if (!captchaToken) {
            setError("Подтвердите, что вы не робот");
            return;
        }
        const normalizedIdentifier = identifier.trim();
        const consentAccepted = (await syncPersonalDataConsentStatus(normalizedIdentifier, "identifier")) || personalDataConsentChecked;
        if (!consentAccepted) {
            setError(LEGAL_CONSENT_ERROR_MESSAGE);
            focusConsent();
            return;
        }
        setLoading(true);
        try {
            const apiBase = resolveApiBase();
            const res = await fetch(`${apiBase}/api/login`, {
                method: "POST",
                headers: withClientHeaders({
                    "Content-Type": "application/json",
                    "X-CSRF-Token": fingerprint,
                }),
                credentials: "include",
                body: JSON.stringify({
                    identifier: normalizedIdentifier,
                    password,
                    fingerprint,
                    captchaToken,
                    personalDataConsentAccepted: consentAccepted,
                    device: buildDevicePayload(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            mergeLegalStatus(readLegalConsentStatus(data));
            if (!res.ok)
                throw new Error(data?.error || "invalid_credentials");
            rememberAcceptedConsent(normalizedIdentifier);
            resetFallbackUi();
            await finalizeLogin(data.access_token, data.refresh_token);
        }
        catch (err) {
            applyMappedError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setLoading(false);
        }
    }
    const phoneConsentIsKnownAccepted = phoneConsentLookupKey !== "" &&
        phoneConsentLookupKey === normalizeConsentLookupValue(phone) &&
        phoneConsentAccepted;
    const identifierConsentIsKnownAccepted = identifierConsentLookupKey !== "" &&
        identifierConsentLookupKey === normalizeConsentLookupValue(identifier) &&
        identifierConsentAccepted;
    const otpConsentRequired = isReadyPhoneForConsentLookup(phone) ? !phoneConsentIsKnownAccepted : true;
    const passwordConsentRequired = isReadyIdentifierForConsentLookup(identifier) ? !identifierConsentIsKnownAccepted : true;
    const activeConsentRequired = authMode === "password" ? passwordConsentRequired : otpConsentRequired;
    const showLegalConsentError = error === LEGAL_CONSENT_ERROR_MESSAGE;
    const showConsentBlock = authMode === "password"
        ? isReadyIdentifierForConsentLookup(identifier) || personalDataConsentChecked || showLegalConsentError || !passwordConsentRequired
        : step === "verify" || isReadyPhoneForConsentLookup(phone) || personalDataConsentChecked || showLegalConsentError || !otpConsentRequired;
    const showCaptcha = authMode === "password" || (authMode === "otp" && step === "request");
    const consentSatisfied = !activeConsentRequired || personalDataConsentChecked;
    const showCaptchaAfterConsent = showCaptcha && consentSatisfied;
    const showConsentBeforeCaptchaHint = showCaptcha && activeConsentRequired && !personalDataConsentChecked;
    const phoneReadyForRequest = isValidPhone(phone);
    const identifierReady = identifier.trim().length > 0;
    const passwordReady = password.length > 0;
    const codeValue = otpCode.replace(/\D/g, "").slice(0, otpCodeLength);
    const isCallMeFlow = authMode === "otp" && step === "verify" && channel === "call_me";
    const showVerifyCaptcha = authMode === "otp" && step === "verify" && verifyCaptchaAction !== null;
    const requestActionsDisabled = loading || otpTTL > 0 || !phoneReadyForRequest || !consentSatisfied || !captchaToken;
    const passwordSubmitDisabled = loading || !identifierReady || !passwordReady || !consentSatisfied || !captchaToken;
    const verifySubmitDisabled = isCallMeFlow ? loading : loading || codeValue.length !== otpCodeLength;
    const phoneFieldError = phoneTouched && phone.trim() !== "" && !phoneReadyForRequest ? "Введите телефон в формате +7 999 123-45-67" : "";
    const codeFieldError = codeTouched && codeValue.length !== otpCodeLength ? `Введите ${formatDigitsLabel(otpCodeLength)}` : "";
    const identifierFieldError = identifierTouched && !identifierReady ? "Укажите email или телефон" : "";
    const passwordFieldError = passwordTouched && !passwordReady ? "Введите пароль" : "";
    const captchaHint = showCaptcha && !captchaToken
        ? authMode === "password"
            ? "Проверка «Я не робот» не выполнена. Подтвердите её, чтобы войти по паролю."
            : "Проверка «Я не робот» не выполнена. Подтвердите её, чтобы отправить код."
        : "";
    const verifyCaptchaHint = showVerifyCaptcha && !captchaToken
        ? buildVerifyCaptchaMessage(verifyCaptchaAction)
        : "";
    const requestHint = otpTTL > 0
        ? `Повторная отправка будет доступна через ${otpTTL} с`
        : channel === "call_me"
            ? "На следующем шаге покажем номер, на который нужно позвонить со своего телефона."
            : channel === "call"
                ? "Мы позвоним на ваш номер и попросим ввести код из входящего звонка."
                : channel === "telegram"
                    ? "Код придёт в Telegram, если он доступен для вашего номера."
                    : !phoneReadyForRequest && phone.trim() !== ""
                        ? "Укажите номер полностью, чтобы отправить код."
                        : "";
    const screenSubtitle = authMode === "password"
        ? "Введите email или телефон и пароль от личного кабинета."
        : step === "verify" && channel === "call_me"
            ? "Это звонок с вашего номера на наш номер. После завершения звонка мы подтвердим вход автоматически."
            : step === "verify"
                ? "Подтвердите вход кодом из сообщения или звонка."
                : "Введите телефон и выберите понятный для вас способ подтверждения входа.";
    return (_jsxs("div", { className: "auth-root", children: [_jsx(AppUpdateGate, {}), _jsxs("div", { className: "auth-card", children: [_jsx(BrandSide, {}), _jsxs("div", { className: "auth-form", children: [_jsxs("header", { className: "auth-header", children: [_jsx("h1", { children: "\u0412\u0445\u043E\u0434 \u0432 \u0427\u0438\u043E \u0427\u0438\u043E" }), _jsx("p", { className: "subtitle", children: screenSubtitle })] }), _jsx(AuthModeTabs, { active: authMode, onChange: handleAuthModeChange }), authMode === "otp" && _jsx(Stepper, { step: step, channel: channel }), error && (_jsx("div", { className: "alert error", role: "alert", children: error })), success && (_jsx("div", { className: "alert success", role: "status", children: success })), authMode === "otp" && step === "request" && (_jsxs(OtpRequestSection, { phone: phone, phoneError: phoneFieldError, requestHint: requestHint, requestActionsDisabled: requestActionsDisabled, channel: channel, otpTTL: otpTTL, phoneInputRef: phoneInputRef, onPhoneChange: (value) => {
                                    handlePhoneChange(value);
                                }, onPhoneBlur: () => {
                                    setPhoneTouched(true);
                                    if (isValidPhone(phone)) {
                                        void syncPersonalDataConsentStatus(normalizePhoneForApi(phone), "phone");
                                    }
                                }, onRequestOtp: (nextChannel) => {
                                    void requestOtp(nextChannel);
                                }, children: [showConsentBlock && (_jsx(LegalConsentBlock, { checked: personalDataConsentChecked, required: activeConsentRequired, status: legalStatus, showError: showLegalConsentError || (consentTouched && activeConsentRequired && !personalDataConsentChecked), inputRef: consentCheckboxRef, onChange: handlePersonalDataConsentChange })), showConsentBeforeCaptchaHint && (_jsx("p", { className: "pre-captcha-hint", children: "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u0435 \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u0441 \u043F\u043E\u043B\u0438\u0442\u0438\u043A\u043E\u0439, \u043F\u043E\u0441\u043B\u0435 \u044D\u0442\u043E\u0433\u043E \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u043F\u0440\u043E\u0432\u0435\u0440\u043A\u0430 \u042F\u043D\u0434\u0435\u043A\u0441\u0430 \u00AB\u042F \u043D\u0435 \u0440\u043E\u0431\u043E\u0442\u00BB." })), showCaptchaAfterConsent && (_jsx(CaptchaPanel, { captchaVersion: captchaVersion, hint: captchaHint, onSuccess: handleCaptchaSuccess }))] })), authMode === "otp" && step === "verify" && (_jsx(OtpVerifySection, { phone: phone, channel: channel, otpCode: otpCode, otpCodeLength: otpCodeLength, otpTTL: otpTTL, codeError: codeFieldError, loading: loading, showNoCodeBtn: showNoCodeBtn, showSendSmsBtn: showSendSmsBtn, showVerifyCaptcha: showVerifyCaptcha, verifyCaptchaHint: verifyCaptchaHint, hasCaptchaToken: Boolean(captchaToken), captchaVersion: captchaVersion, canSubmit: !verifySubmitDisabled, codeInputRef: codeInputRef, onOtpCodeChange: (value) => {
                                    setOtpCode(value.replace(/\D/g, "").slice(0, otpCodeLength));
                                }, onCodeBlur: () => setCodeTouched(true), onBack: () => {
                                    resetOtpFlowToRequest();
                                }, onResend: () => {
                                    void requestOtp(channel);
                                }, onOpenDialer: handleOpenCallMeDialer, onCopyRoutingKey: () => {
                                    void handleCopyCallMeRoutingKey();
                                }, callMeRoutingKey: callMeRoutingKey, callMeStatus: callMeStatus, onCheckStatus: () => {
                                    clearFeedback();
                                    callMePollInFlightRef.current = false;
                                    void pollCallMeStatus({ manual: true });
                                }, onOpenSmsFallback: () => setShowSendSmsBtn(true), onCancelSmsFallback: () => {
                                    setShowSendSmsBtn(false);
                                    setVerifyCaptchaAction(null);
                                }, onRequestSmsFallback: () => {
                                    void requestOtp("sms", { force: true, fromFallback: true });
                                }, onCaptchaSuccess: handleCaptchaSuccess, onSubmit: verifyOtp })), authMode === "password" && (_jsxs(PasswordPanel, { identifier: identifier, password: password, showPwd: showPwd, loading: loading, canSubmit: !passwordSubmitDisabled, identifierError: identifierFieldError, passwordError: passwordFieldError, identifierInputRef: identifierInputRef, passwordInputRef: passwordInputRef, onIdentifierChange: (value) => {
                                    handleIdentifierChange(value);
                                }, onIdentifierBlur: () => {
                                    setIdentifierTouched(true);
                                    if (identifier.trim()) {
                                        void syncPersonalDataConsentStatus(identifier, "identifier");
                                    }
                                }, onPasswordChange: (value) => {
                                    setPassword(value);
                                }, onPasswordBlur: () => setPasswordTouched(true), onShowPasswordToggle: () => setShowPwd((prev) => !prev), onSubmit: passwordLogin, children: [showConsentBlock && (_jsx(LegalConsentBlock, { checked: personalDataConsentChecked, required: activeConsentRequired, status: legalStatus, showError: showLegalConsentError || (consentTouched && activeConsentRequired && !personalDataConsentChecked), inputRef: consentCheckboxRef, onChange: handlePersonalDataConsentChange })), showConsentBeforeCaptchaHint && (_jsx("p", { className: "pre-captcha-hint", children: "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u0435 \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u0441 \u043F\u043E\u043B\u0438\u0442\u0438\u043A\u043E\u0439, \u043F\u043E\u0441\u043B\u0435 \u044D\u0442\u043E\u0433\u043E \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u043F\u0440\u043E\u0432\u0435\u0440\u043A\u0430 \u042F\u043D\u0434\u0435\u043A\u0441\u0430 \u00AB\u042F \u043D\u0435 \u0440\u043E\u0431\u043E\u0442\u00BB." })), showCaptchaAfterConsent && (_jsx(CaptchaPanel, { captchaVersion: captchaVersion, hint: captchaHint, onSuccess: handleCaptchaSuccess }))] })), authMode === "otp" && (_jsx("p", { className: "tos", children: isCallMeFlow
                                    ? "Подтверждение по звонку действует 5 минут. Если не сработало, можно переключиться на SMS."
                                    : "Код действует 5 минут. Никому не сообщайте его." })), LOGIN_DEBUG && (_jsxs(_Fragment, { children: [_jsx(DebugTools, { showDeviceDebug: showDeviceDebug, onToggleDeviceDebug: () => setShowDeviceDebug((prev) => !prev), debugPayload: {
                                            inWebView: isInWebView(),
                                            expoPushToken,
                                            nativeDevice,
                                            derivedVersion: getVersionFromDevice(nativeDevice),
                                            legalStatus,
                                            personalDataConsentChecked,
                                            activeConsentRequired,
                                            authMode,
                                            step,
                                            callMe: {
                                                attemptId: callMeAttemptId,
                                                routingKey: callMeRoutingKey,
                                                status: callMeStatus,
                                            },
                                            fallback: {
                                                showNoCodeBtn,
                                                showSendSmsBtn,
                                                afterSec: FALLBACK_SMS_AFTER_SEC,
                                            },
                                        } }), _jsx(FooterNote, { fingerprint: fingerprint })] }))] })] })] }));
}
function BrandSide() {
    return (_jsxs("div", { className: "brand", children: [_jsx("div", { className: "logo", children: "\u0427\u0438\u043E \u0427\u0438\u043E" }), _jsx("h2", { children: "\u0421\u0435\u0440\u0432\u0438\u0441 \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u0438" }), _jsx("p", { children: "\u0412\u043E\u0439\u0434\u0438\u0442\u0435 \u0432 \u043B\u0438\u0447\u043D\u044B\u0439 \u043A\u0430\u0431\u0438\u043D\u0435\u0442, \u0447\u0442\u043E\u0431\u044B \u0443\u043F\u0440\u0430\u0432\u043B\u044F\u0442\u044C \u0437\u0430\u043F\u0438\u0441\u044C\u044E \u0438 \u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u044C\u0441\u044F \u0441\u0435\u0440\u0432\u0438\u0441\u0430\u043C\u0438 \u0427\u0438\u043E \u0427\u0438\u043E." })] }));
}
function AuthModeTabs(props) {
    const { active, onChange } = props;
    return (_jsxs("div", { className: "auth-tabs", role: "tablist", "aria-label": "\u0421\u043F\u043E\u0441\u043E\u0431 \u0432\u0445\u043E\u0434\u0430", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": active === "otp", className: `auth-tab ${active === "otp" ? "active" : ""}`, onClick: () => onChange("otp"), children: "\u041F\u043E \u043A\u043E\u0434\u0443" }), _jsx("button", { type: "button", role: "tab", "aria-selected": active === "password", className: `auth-tab ${active === "password" ? "active" : ""}`, onClick: () => onChange("password"), children: "\u041F\u043E \u043F\u0430\u0440\u043E\u043B\u044E" })] }));
}
function Stepper({ step, channel }) {
    return (_jsxs("div", { className: "stepper", "aria-label": "\u0428\u0430\u0433\u0438 \u0432\u0445\u043E\u0434\u0430 \u043F\u043E \u043A\u043E\u0434\u0443", children: [_jsxs("div", { className: `step ${step === "request" ? "active" : ""}`, children: [_jsx("span", { children: "1" }), " \u041D\u043E\u043C\u0435\u0440"] }), _jsx("div", { className: "sep" }), _jsxs("div", { className: `step ${step === "verify" ? "active" : ""}`, children: [_jsx("span", { children: "2" }), " ", channel === "call_me" && step === "verify" ? "Звонок" : "Код"] })] }));
}
function ChannelBtn(props) {
    const { text, description, active, disabled, onClick } = props;
    return (_jsxs("button", { type: "button", className: `channel-btn ${active ? "active" : ""}`, onClick: onClick, disabled: disabled, children: [_jsx("span", { className: "channel-btn__title", children: text }), _jsx("span", { className: "channel-btn__desc", children: description })] }));
}
function OtpRequestSection(props) {
    const { phone, phoneError, requestHint, requestActionsDisabled, channel, otpTTL, phoneInputRef, onPhoneChange, onPhoneBlur, onRequestOtp, children, } = props;
    const channelNote = getChannelSelectionNote(channel);
    return (_jsxs("section", { "aria-label": "\u0428\u0430\u0433 1: \u043D\u043E\u043C\u0435\u0440 \u0438 \u0441\u043F\u043E\u0441\u043E\u0431", children: [_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "phone", children: "\u0422\u0435\u043B\u0435\u0444\u043E\u043D" }), _jsx("input", { ref: phoneInputRef, id: "phone", type: "tel", inputMode: "tel", autoComplete: "tel", placeholder: "+7 999 123-45-67", value: phone, "aria-invalid": Boolean(phoneError), onChange: (e) => onPhoneChange(e.target.value), onBlur: onPhoneBlur }), phoneError && _jsx("p", { className: "field-error", children: phoneError })] }), children, _jsxs("div", { className: "field", children: [_jsx("label", { children: "\u041A\u0430\u043A \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C \u0432\u0445\u043E\u0434" }), _jsxs("div", { className: "channel-group", children: [_jsx(ChannelBtn, { text: "\u041A\u043E\u0434 \u0437\u0432\u043E\u043D\u043A\u043E\u043C", description: getChannelDescription("call"), active: channel === "call", onClick: () => onRequestOtp("call"), disabled: requestActionsDisabled }), _jsx(ChannelBtn, { text: "\u041F\u043E\u0437\u0432\u043E\u043D\u044E \u0441\u0430\u043C", description: getChannelDescription("call_me"), active: channel === "call_me", onClick: () => onRequestOtp("call_me"), disabled: requestActionsDisabled }), _jsx(ChannelBtn, { text: "Telegram", description: getChannelDescription("telegram"), active: channel === "telegram", onClick: () => onRequestOtp("telegram"), disabled: requestActionsDisabled })] }), requestHint && _jsx("p", { className: `status-hint ${otpTTL > 0 ? "muted" : ""}`, children: requestHint }), _jsxs("div", { className: "channel-note", role: "note", children: [_jsx("strong", { children: channelNote.title }), _jsx("p", { children: channelNote.text })] })] })] }));
}
function OtpVerifySection(props) {
    const { phone, channel, otpCode, otpCodeLength, otpTTL, codeError, loading, showNoCodeBtn, showSendSmsBtn, showVerifyCaptcha, verifyCaptchaHint, hasCaptchaToken, captchaVersion, canSubmit, codeInputRef, onOtpCodeChange, onCodeBlur, onBack, onResend, onOpenDialer, onCopyRoutingKey, onOpenSmsFallback, onCancelSmsFallback, onRequestSmsFallback, onCaptchaSuccess, onSubmit, callMeRoutingKey, callMeStatus, onCheckStatus, } = props;
    const verifyChannelHelp = getVerifyChannelHelp(channel);
    if (channel === "call_me") {
        const formattedRoutingKey = formatPhoneInput(callMeRoutingKey) || callMeRoutingKey;
        const callMeStatusText = callMeStatus === "authorized"
            ? "Звонок подтверждён. Завершаем вход…"
            : callMeStatus === "finalizing"
                ? "Подтверждаем сессию…"
                : callMeStatus === "expired"
                    ? "Время ожидания истекло."
                    : "Ждём завершения звонка и подтверждения входа.";
        return (_jsxs("section", { "aria-label": "\u0428\u0430\u0433 2: \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435 \u0437\u0432\u043E\u043D\u043A\u043E\u043C", children: [_jsxs("p", { className: "subtitle", children: ["\u042D\u0442\u043E \u043D\u0435 \u0432\u0445\u043E\u0434\u044F\u0449\u0438\u0439 \u0437\u0432\u043E\u043D\u043E\u043A. \u041F\u043E\u0437\u0432\u043E\u043D\u0438\u0442\u0435 \u0441\u043E \u0441\u0432\u043E\u0435\u0433\u043E \u043D\u043E\u043C\u0435\u0440\u0430 ", _jsx("strong", { children: maskPhone(phone) }), " \u043D\u0430", " ", _jsx("button", { type: "button", className: "callme-number", onClick: onOpenDialer, children: formattedRoutingKey }), ". \u041F\u043E\u0441\u043B\u0435 \u0437\u0430\u0432\u0435\u0440\u0448\u0435\u043D\u0438\u044F \u0437\u0432\u043E\u043D\u043A\u0430 \u043C\u044B \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u043C \u0432\u0445\u043E\u0434 \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438."] }), _jsx("p", { className: "status-hint", children: "\u0415\u0441\u043B\u0438 \u0437\u0432\u043E\u043D\u043E\u043A \u043D\u0435 \u043E\u0442\u043A\u0440\u044B\u043B\u0441\u044F \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438, \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \u043A\u043D\u043E\u043F\u043A\u0443 \u043D\u0438\u0436\u0435 \u0438\u043B\u0438 \u043F\u043E\u0437\u0432\u043E\u043D\u0438\u0442\u0435 \u043D\u0430 \u0443\u043A\u0430\u0437\u0430\u043D\u043D\u044B\u0439 \u043D\u043E\u043C\u0435\u0440 \u0432\u0440\u0443\u0447\u043D\u0443\u044E." }), _jsxs("div", { className: "callme-box", children: [_jsx("button", { type: "button", className: "primary callme-action", onClick: onOpenDialer, children: "\u041F\u043E\u0437\u0432\u043E\u043D\u0438\u0442\u044C" }), _jsx("button", { type: "button", className: "outline callme-copy", onClick: onCopyRoutingKey, disabled: loading, children: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043D\u043E\u043C\u0435\u0440" }), _jsx("p", { className: `status-hint ${callMeStatus === "expired" ? "status-hint--warning" : ""}`, children: callMeStatusText })] }), _jsxs("div", { className: "row auth-actions", children: [_jsx("button", { type: "button", className: "outline", onClick: onBack, disabled: loading, children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u043D\u043E\u043C\u0435\u0440" }), _jsx("button", { type: "button", className: "outline", onClick: onCheckStatus, disabled: loading || callMeStatus === "finalizing", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C \u0441\u0442\u0430\u0442\u0443\u0441" })] }), _jsxs("div", { className: "fallback-box", children: [showNoCodeBtn && !showSendSmsBtn && (_jsx("button", { type: "button", className: "link", onClick: onOpenSmsFallback, disabled: loading, children: "\u041D\u0435 \u043F\u043E\u043B\u0443\u0447\u0430\u0435\u0442\u0441\u044F \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C?" })), showSendSmsBtn && (_jsxs("div", { className: "row auth-actions", children: [_jsx("button", { type: "button", className: "outline", onClick: onRequestSmsFallback, disabled: loading || (showVerifyCaptcha && !hasCaptchaToken), children: "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C SMS" }), _jsx("button", { type: "button", className: "link", onClick: onCancelSmsFallback, disabled: loading, children: "\u041E\u0442\u043C\u0435\u043D\u0430" })] })), showVerifyCaptcha && (_jsx(CaptchaPanel, { captchaVersion: captchaVersion, hint: verifyCaptchaHint, onSuccess: onCaptchaSuccess }))] })] }));
    }
    return (_jsxs("form", { onSubmit: onSubmit, noValidate: true, "aria-label": "\u0428\u0430\u0433 2: \u0432\u0432\u043E\u0434 \u043A\u043E\u0434\u0430", children: [_jsxs("p", { className: "subtitle", children: ["\u041C\u044B \u043E\u0442\u043F\u0440\u0430\u0432\u0438\u043B\u0438 \u043A\u043E\u0434 \u043D\u0430 ", _jsx("strong", { children: maskPhone(phone) }), " \u0447\u0435\u0440\u0435\u0437 ", labelChannel(channel), "."] }), verifyChannelHelp && _jsx("p", { className: "status-hint status-hint--warning", children: verifyChannelHelp }), _jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "code", children: channel === "call" ? "Код из звонка" : "Код из сообщения" }), _jsx("input", { ref: codeInputRef, id: "code", name: "otp", type: "tel", inputMode: "numeric", pattern: "[0-9]*", enterKeyHint: "done", autoComplete: "one-time-code", placeholder: formatDigitsLabel(otpCodeLength), value: otpCode, "aria-invalid": Boolean(codeError), onChange: (e) => onOtpCodeChange(e.target.value), onBlur: onCodeBlur, autoFocus: true }), codeError && _jsx("p", { className: "field-error", children: codeError })] }), _jsxs("div", { className: "row auth-actions", children: [_jsx("button", { type: "button", className: "outline", onClick: onBack, disabled: loading, children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u043D\u043E\u043C\u0435\u0440" }), _jsx("button", { type: "button", className: "outline", onClick: onResend, disabled: loading || otpTTL > 0 || (showVerifyCaptcha && !hasCaptchaToken), children: otpTTL > 0 ? `Отправить снова через ${otpTTL}с` : "Отправить код снова" }), _jsx("button", { className: "primary", type: "submit", formNoValidate: true, disabled: !canSubmit, children: loading ? "Проверяем…" : "Войти" })] }), showVerifyCaptcha && (_jsx("div", { className: "fallback-box", children: _jsx(CaptchaPanel, { captchaVersion: captchaVersion, hint: verifyCaptchaHint, onSuccess: onCaptchaSuccess }) })), (channel === "call" || channel === "telegram") && (_jsxs("div", { className: "fallback-box", children: [showNoCodeBtn && !showSendSmsBtn && (_jsx("button", { type: "button", className: "link", onClick: onOpenSmsFallback, disabled: loading, children: "\u041D\u0435 \u043F\u0440\u0438\u0448\u0435\u043B \u043A\u043E\u0434?" })), showSendSmsBtn && (_jsxs("div", { className: "row auth-actions", children: [_jsx("button", { type: "button", className: "outline", onClick: onRequestSmsFallback, disabled: loading || (showVerifyCaptcha && !hasCaptchaToken), children: "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C SMS" }), _jsx("button", { type: "button", className: "link", onClick: onCancelSmsFallback, disabled: loading, children: "\u041E\u0442\u043C\u0435\u043D\u0430" })] }))] }))] }));
}
function CaptchaPanel(props) {
    const { captchaVersion, hint, onSuccess } = props;
    const isPending = Boolean(hint);
    return (_jsxs("div", { className: "captcha-panel", children: [_jsx("div", { className: "field captcha-wrap", children: _jsx(SmartCaptcha, { sitekey: CAPTCHA_SITEKEY, onSuccess: onSuccess }, `captcha-${captchaVersion}`) }), hint && _jsx("p", { className: `status-hint ${isPending ? "status-hint--warning" : ""}`, children: hint })] }));
}
function LegalConsentBlock(props) {
    const { checked, required, status, showError, inputRef, onChange } = props;
    return (_jsxs("div", { className: `legal-box ${required ? "" : "legal-box--accepted"} ${showError ? "legal-box--error" : ""}`, children: [required ? (_jsxs("label", { className: "legal-checkbox", children: [_jsx("input", { ref: inputRef, type: "checkbox", checked: checked, onChange: (e) => onChange(e.target.checked) }), _jsx("span", { children: "\u042F \u043E\u0437\u043D\u0430\u043A\u043E\u043C\u0438\u043B\u0441\u044F(\u0430\u0441\u044C) \u0441 \u041F\u043E\u043B\u0438\u0442\u0438\u043A\u043E\u0439 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0438 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 \u0438 \u0434\u0430\u044E \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u043D\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0443 \u043C\u043E\u0438\u0445 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430 \u0438 \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u0438\u044F \u0441\u0435\u0440\u0432\u0438\u0441\u043E\u0432 \u0427\u0438\u043E \u0427\u0438\u043E." })] })) : (_jsx("p", { className: "legal-note", children: "\u0421\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u043D\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0443 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 \u0443\u0436\u0435 \u0437\u0430\u0444\u0438\u043A\u0441\u0438\u0440\u043E\u0432\u0430\u043D\u043E \u0434\u043B\u044F \u044D\u0442\u043E\u0433\u043E \u0432\u0445\u043E\u0434\u0430." })), _jsxs("p", { className: "legal-links", children: [_jsx("a", { href: status.documentUrl, target: "_blank", rel: "noreferrer", children: "\u0421\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u043D\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0443 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445" }), " · ", _jsx("a", { href: status.privacyPolicyUrl, target: "_blank", rel: "noreferrer", children: "\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0438 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445" }), status.userAgreementUrl ? (_jsxs(_Fragment, { children: [" · ", _jsx("a", { href: status.userAgreementUrl, target: "_blank", rel: "noreferrer", children: "\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044C\u0441\u043A\u043E\u0435 \u0441\u043E\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435" })] })) : null] }), showError && _jsx("p", { className: "legal-inline-error", children: "\u041D\u0443\u0436\u043D\u043E \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u0435, \u0447\u0442\u043E\u0431\u044B \u043F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C \u0432\u0445\u043E\u0434." })] }));
}
function PasswordPanel(props) {
    const { identifier, password, showPwd, loading, canSubmit, identifierError, passwordError, identifierInputRef, passwordInputRef, onIdentifierChange, onIdentifierBlur, onPasswordChange, onPasswordBlur, onShowPasswordToggle, onSubmit, children, } = props;
    return (_jsxs("form", { className: "password-form", onSubmit: onSubmit, noValidate: true, children: [_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "identifier", children: "Email \u0438\u043B\u0438 \u0442\u0435\u043B\u0435\u0444\u043E\u043D" }), _jsx("input", { ref: identifierInputRef, id: "identifier", type: "text", inputMode: "text", autoComplete: "username", placeholder: "name@domain.com \u0438\u043B\u0438 +7 999 123-45-67", value: identifier, "aria-invalid": Boolean(identifierError), onChange: (e) => onIdentifierChange(e.target.value), onBlur: onIdentifierBlur }), identifierError && _jsx("p", { className: "field-error", children: identifierError })] }), _jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "password", children: "\u041F\u0430\u0440\u043E\u043B\u044C" }), _jsxs("div", { className: "pwd-wrap", children: [_jsx("input", { ref: passwordInputRef, id: "password", type: showPwd ? "text" : "password", autoComplete: "current-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", value: password, "aria-invalid": Boolean(passwordError), onChange: (e) => onPasswordChange(e.target.value), onBlur: onPasswordBlur }), _jsx("button", { type: "button", className: "ghost", onClick: onShowPasswordToggle, "aria-label": "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u043F\u0430\u0440\u043E\u043B\u044C", children: showPwd ? "Скрыть" : "Показать" })] }), passwordError && _jsx("p", { className: "field-error", children: passwordError })] }), children, _jsx("button", { className: "primary block", type: "submit", disabled: !canSubmit, children: loading ? "Входим…" : "Войти по паролю" })] }));
}
function DebugTools(props) {
    const { showDeviceDebug, onToggleDeviceDebug, debugPayload } = props;
    return (_jsxs("div", { className: "device-debug", children: [_jsx("button", { type: "button", className: "link", onClick: onToggleDeviceDebug, children: showDeviceDebug ? "Скрыть информацию об устройстве" : "Показать информацию об устройстве" }), showDeviceDebug && (_jsx("pre", { className: "device-debug__pre", children: JSON.stringify(debugPayload, null, 2) }))] }));
}
const FooterNote = ({ fingerprint }) => {
    return (_jsxs("div", { className: "footer-note", children: [_jsxs("code", { className: "fp", title: "fingerprint \u0434\u043B\u044F \u0437\u0430\u0449\u0438\u0442\u044B refresh", children: ["fp: ", fingerprint.slice(0, 24), "\u2026"] }), _jsx("button", { className: "link", onClick: async () => {
                    const apiBase = resolveApiBase();
                    const res = await fetch(`${apiBase}/api/refresh`, {
                        method: "POST",
                        credentials: "include",
                        headers: { "X-CSRF-Token": fingerprint },
                    });
                    const data = await res.json().catch(() => ({}));
                    if (res.ok && data.access_token) {
                        try {
                            sessionStorage.setItem("access_token", data.access_token);
                        }
                        catch { }
                        if (data.refresh_token) {
                            try {
                                sessionStorage.setItem("refresh_token", data.refresh_token);
                            }
                            catch { }
                        }
                        alert("Access токен обновлён");
                    }
                    else {
                        alert("Ошибка refresh: " + (data?.error || res.status));
                    }
                }, children: "\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C refresh" })] }));
};
